import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = mdoc => mdoc.id;

export const meetingDocumentSlice = createSlice({
    name: 'meetingDocs',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            state.data[action.payload.meetingid] = action.payload.data; 
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byMeeting = groupBy(toUpdate, 'meetingid');
            Object.entries(byMeeting).forEach(([ meetingid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                state.data[meetingid] = ((state.data[meetingid]?.filter(mdoc => !ids.has(key(mdoc)))) ?? []).concat(docs);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byMeeting = groupBy(toRemove, 'meetingid');
            Object.entries(byMeeting).forEach(([ meetingid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                if (state.data[meetingid]) {
                    state.data[meetingid] = state.data[meetingid].filter(mdoc => !ids.has(key(mdoc)));
                    if (state.data[meetingid].length === 0) {
                        delete state.data[meetingid];
                    }
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingDocumentSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffDocs', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffDocs');

const fetchMeetingDocs = meetingid => network => network.get('/api/v1/meetingDocs/' + encodeURIComponent(meetingid), data => meetingDocumentSlice.actions.set({ meetingid, data}), visualizeFailure('get'));
export const fetchMeetingDoc = async (network, id) => network.fetchData('/api/v1/meetingDoc/' + encodeURIComponent(id));
export const addMeetingDoc = (network, doc) => {    
    let data = new FormData();
    data.append('meetingid', doc.meetingid)
    data.append('file', doc.file);
    data.append('filename', doc.filename);
    data.append('member_accessible', doc.member_accessible);
    return network.postData('/api/v1/meetingDoc', data)
};
export const updateMeetingDoc = (network, data) => network.put('/api/v1/meetingDoc', data, meetingDocumentSlice.addOrUpdate, visualizeFailure('update'));
export const removeMeetingDoc = (network, id) => network.delete(`/api/v1/meetingDoc/${encodeURIComponent(id)}`, meetingDocumentSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectAllMeetingDocData = state => state.meetingDocs.data;
const selectMeetingDocs = meetingid => createSelector([selectAllMeetingDocData], docs => docs[meetingid] ?? []);
// #endregion

const listen = {
    meetingDocCreated: (doc, { dispatch, websocket }) => {
        markFetched(websocket, 'meeting_doc', key(doc))
        dispatch(meetingDocumentSlice.actions.addOrUpdate(doc))
    },
    meetingDocRemoved: (doc, { dispatch }) => dispatch(meetingDocumentSlice.actions.remove(doc)),
}

const meetingInitDocsByID = initMapFactory(fetchMeetingDocs, listen)
registerEntityBasedFetch('meeting', meetingInitDocsByID);

const cleanup = { root: meetingDocumentSlice.name, all: state => Object.values(selectAllMeetingDocData(state)).flat(), removeAll: meetingDocumentSlice.actions.remove, key };
export const [useMeetingDocs] = dataHookFactory(selectMeetingDocs, meetingInitDocsByID, {cleanup});

const reducer = { name: meetingDocumentSlice.name, reducer: meetingDocumentSlice.reducer }
export default reducer;